<template>
  <div class="use-rebuy">
    <div
      :data-rebuy-id="widgetId"
      v-bind="additionalData"
    />
  </div>
</template>

<script>

/**
 * Vue: Use rebuy (use-rebuy)
 * -----------------------------------------------------------------------------
 * Use events and rebuy widgets to provide data to Vue components.
 *
 */

import productsQuery from '~graphql/products.gql'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatProduct } from '~/helpers/format'

export default {
  name: 'UseRebuy',

  props: {
    additionalData: {
      type: Object,
      default: () => ({}),
    },
    widgetId: {
      type: String,
      default: '',
    },
  },

  emits: {
    widget: (payload) => typeof payload === 'object',
    products: (payload) => typeof payload === 'object',
    title: (payload) => typeof payload === 'string',
  },

  data() {
    return {
      events: ['rebuy.productsChange'],
      cache: {},
      widget: null,
    }
  },

  mounted() {
    this.setEventListeners()
    this.init()
  },

  beforeUnmount() {
    this.getWidget()
    if (this.widget?.detachWidget) {
      this.widget?.detachWidget()
    }
    this.events.forEach(
      (eventName) => document.removeEventListener(eventName, this.handleWidgetUpdate),
    )
  },

  methods: {

    /**
     * Set event listeners
     */
    setEventListeners() {
      this.events.forEach(
        (eventName) => document.addEventListener(eventName, this.handleWidgetUpdate),
      )
    },

    /**
     * Initialize Rebuy
     */
    init() {
      const { Rebuy } = window
      if (!Rebuy?.init) {
        document.addEventListener('rebuy.ready', this.onRebuyReady)
        return
      }
      this.onRebuyReady()
    },

    /**
     * Assign reactive Rebuy variable when ready.
     */
    onRebuyReady() {
      window.Rebuy.init()
      document.removeEventListener('rebuy.ready', this.onRebuyReady)
      this.getWidget()
      this.emitWidget()
    },

    /**
     * Get widget from Rebuy widgets array
     */
    getWidget() {
      this.widget = window.Rebuy?.widgets?.find(
        (_widget) => _widget.id === this.widgetId,
      )
    },

    /**
     * Emit widget data
     */
    emitWidget() {
      const widget = this.widget
      if (!widget) {
        return
      }

      const { products, settings } = widget.data

      this.$emit('widget', widget.data)

      if (products) {
        this.cacheProducts(products)
      }

      if (settings?.language?.title) {
        this.$emit('title', settings.language.title)
      }
    },

    /**
     * Update widget if a change event was detected
     * @param event
     */
    handleWidgetUpdate(event) {
      if (!this.isEventValid(event)) {
        return
      }
      this.getWidget()
      this.emitWidget()
    },

    /**
     * Check if widget from event is relevant to this component
     * @param event
     * @returns {boolean}
     */
    isEventValid(event) {
      const { widget } = event?.detail || {}
      return widget?.id === this.widgetId
    },

    /**
     * Get Rebuy products from Storefront API or use from cache.
     * Save new products to this component's cache.
     * @param products
     */
    async cacheProducts(products) {
      let response = null
      const formattedProducts = []
      const ids = []
      products.forEach((product) => {
        if (!product?.id) {
          return
        }
        const cached = this.cache[product.id]
        if (cached) {
          formattedProducts.push(cached)
          return
        }
        ids.push(`gid://shopify/Product/${product.id}`)
      })

      if (!ids.length) {
        this.$emit('products', formattedProducts)
        return
      }

      try {
        response = await cnvs.Query({
          query: productsQuery,
          variables: getVariables({
            ids,
            metafieldIds: [
              ...getMetafieldIDs('product'),
              ...getMetafieldIDs('variant'),
            ],
          }),
        })

        const cache = { ...this.cache }

        response.nodes.forEach((product) => {
          const formatted = formatProduct({ product })
          if (!formatted?.id) {
            return
          }
          cache[formatted.id] = formatted
          formattedProducts.push(formatted)
        })

        this.cache = cache
        this.$emit('products', formattedProducts)
      } catch (error) {
        cnvs.ReportError('[Use Rebuy - Product Query]', error)
        cnvs.ReportError('[Use Rebuy - Product Query Response]', response)
      }
    },
  },
}
</script>

<style lang="scss">
@import './use-rebuy';
</style>
